import apiClient from '../client';
import type { Database } from '@/lib/supabase/supabase-types';

type Course = Database['public']['Tables']['courses']['Row'];
type CourseInsert = Database['public']['Tables']['courses']['Insert'];
type CourseUpdate = Database['public']['Tables']['courses']['Update'];
type CourseEnrollment = Database['public']['Tables']['course_enrollments']['Row'];

export const courseService = {
  /**
   * Get all courses (public - only published courses by default)
   * @param publishedOnly - If false, returns all courses (requires admin auth)
   */
  getCourses: (publishedOnly: boolean = true): Promise<Course[]> => {
    const url = publishedOnly ? '/courses/published' : '/courses?publishedOnly=false';
    return apiClient.get<Course[]>(url) as unknown as Promise<Course[]>;
  },
  
  /**
   * Get all published courses (public endpoint)
   */
  getPublishedCourses: (): Promise<Course[]> => 
    apiClient.get<Course[]>('/courses/published') as unknown as Promise<Course[]>,
  
  /**
   * Get course by ID
   */
  getCourse: (courseId: string): Promise<Course> => 
    apiClient.get<Course>(`/courses/${courseId}`) as unknown as Promise<Course>,
  
  /**
   * Get course by slug
   */
  getCourseBySlug: (slug: string): Promise<Course> => 
    apiClient.get<Course>(`/courses/slug/${slug}`) as unknown as Promise<Course>,
  
  /**
   * Get enrollments for a course
   */
  getCourseEnrollments: (courseId: string): Promise<CourseEnrollment[]> => 
    apiClient.get<CourseEnrollment[]>(`/courses/${courseId}/enrollments`) as unknown as Promise<CourseEnrollment[]>,
  
  /**
   * Get lesson count for a course
   */
  getCourseLessonCount: (courseId: string): Promise<number> => 
    (apiClient.get<{ count: number }>(`/courses/${courseId}/lessons/count`) as unknown as Promise<{ count: number }>).then(res => res.count),
  
  /**
   * Get course sections for a course
   */
  getCourseSections: (courseId: string): Promise<Database['public']['Tables']['course_sections']['Row'][]> => 
    apiClient.get<Database['public']['Tables']['course_sections']['Row'][]>(`/courses/${courseId}/sections`) as unknown as Promise<Database['public']['Tables']['course_sections']['Row'][]>,
  
  /**
   * Get lessons for a course
   */
  getCourseLessons: (courseId: string): Promise<Database['public']['Tables']['lessons']['Row'][]> => 
    apiClient.get<Database['public']['Tables']['lessons']['Row'][]>(`/courses/${courseId}/lessons`) as unknown as Promise<Database['public']['Tables']['lessons']['Row'][]>,
  
  /**
   * Enroll current user in a course
   */
  enrollInCourse: (courseId: string): Promise<CourseEnrollment> => 
    apiClient.post<CourseEnrollment>(`/courses/${courseId}/enroll`) as unknown as Promise<CourseEnrollment>,
  
  /**
   * Get current user's enrollments
   */
  getMyEnrollments: (): Promise<CourseEnrollment[]> => 
    apiClient.get<CourseEnrollment[]>('/courses/enrollments/me') as unknown as Promise<CourseEnrollment[]>,
  
  /**
   * Create a new course (admin only)
   */
  createCourse: (data: CourseInsert): Promise<Course> => 
    apiClient.post<Course>('/courses', data) as unknown as Promise<Course>,
  
  /**
   * Update a course (admin only)
   */
  updateCourse: (courseId: string, data: CourseUpdate): Promise<Course> => 
    apiClient.patch<Course>(`/courses/${courseId}`, data) as unknown as Promise<Course>,
  
  /**
   * Delete a course (admin only)
   */
  deleteCourse: (courseId: string): Promise<void> => 
    apiClient.delete(`/courses/${courseId}`),
};

